import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import ArticleCard from "@/components/feature/ArticleCard";
import SharePanel from "@/pages/detail/components/SharePanel";
import { useSeo } from "@/utils/seo";
import { useFavorites } from "@/hooks/useFavorites";
import { formatDate, formatViews } from "@/utils/format";
import {
  getArticleById,
  getCategoryBySlug,
  getCategoryName,
  getHotArticles,
  getRelatedArticles,
} from "@/utils/articles";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id);
  const [shareOpen, setShareOpen] = useState(false);
  const { toggle, isFavorite } = useFavorites();

  useSeo({
    title: article ? `${article.title} - 黑料` : "内容不存在 - 黑料",
    description: article
      ? article.summary
      : "没有找到这条黑料内容，可能已被移除或链接有误。",
    keywords: article
      ? `${article.tags.join(",")},黑料,${getCategoryName(article.category)}`
      : "黑料,娱乐八卦",
    canonicalPath: `/detail/${id ?? ""}`,
  });

  if (!article) {
    return (
      <SiteLayout>
        <div className="px-4 md:px-6 py-24 flex flex-col items-center text-center">
          <span className="w-16 h-16 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mb-5">
            <i className="ri-error-warning-line text-3xl" />
          </span>
          <h1 className="font-heading font-extrabold text-2xl mb-3">这条内容不见了</h1>
          <p className="text-foreground-600 mb-6 max-w-md">
            你访问的黑料可能已被移除，或者链接有误。不妨回到首页看看最新的爆料内容。
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const category = getCategoryBySlug(article.category);
  const related = getRelatedArticles(article, 4);
  const hotList = getHotArticles(6).filter((item) => item.id !== article.id).slice(0, 5);
  const favorited = isFavorite(article.id);

  return (
    <SiteLayout>
      <div className="px-4 md:px-6 py-5 md:py-8">
        <nav className="flex items-center gap-1.5 text-xs md:text-sm text-foreground-500 mb-5 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line" />
          {category && (
            <>
              <Link
                to={`/category/${category.slug}`}
                className="hover:text-primary-600 transition cursor-pointer"
              >
                {category.name}
              </Link>
              <i className="ri-arrow-right-s-line" />
            </>
          )}
          <span className="text-foreground-900 line-clamp-1">{article.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-8">
          <article className="min-w-0">
            <div className="flex items-center gap-2 mb-4">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="px-2.5 py-1 rounded bg-primary-500 text-background-50 text-xs font-medium cursor-pointer"
                >
                  {category.name}
                </Link>
              )}
              <span className="px-2.5 py-1 rounded bg-accent-100 text-accent-800 text-xs">
                独家揭秘
              </span>
            </div>

            <h1 className="font-heading font-extrabold text-2xl md:text-3xl lg:text-4xl leading-tight mb-4">
              {article.title}
            </h1>

            <p className="text-base text-foreground-600 leading-relaxed mb-5 pb-5 border-b border-background-200">
              {article.summary}
            </p>

            <div className="flex items-center gap-4 flex-wrap text-sm text-foreground-500 mb-6">
              <span className="flex items-center gap-1.5">
                <span className="w-7 h-7 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center">
                  <i className="ri-user-line text-sm" />
                </span>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {formatDate(article.date)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" />
                {formatViews(article.views)} 次浏览
              </span>
            </div>

            <div className="w-full rounded-lg overflow-hidden mb-6 bg-background-200">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 黑料资讯`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="space-y-4 text-base leading-loose text-foreground-800">
              {article.content.map((paragraph, i) => (
                <p key={`p-${i}`} className="indent-8">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs hover:bg-secondary-200 transition cursor-pointer"
                >
                  # {tag}
                </Link>
              ))}
            </div>

            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => toggle(article.id)}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium transition cursor-pointer whitespace-nowrap ${
                  favorited
                    ? "bg-accent-500 text-foreground-950"
                    : "border border-background-300 hover:border-accent-400 hover:text-accent-700"
                }`}
              >
                <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                {favorited ? "已收藏" : "收藏这条"}
              </button>
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-forward-line" />
                分享给朋友
              </button>
            </div>

            <div className="mt-8">
              <h2 className="font-heading font-extrabold text-lg mb-4 flex items-center gap-2">
                <i className="ri-sparkling-2-line text-primary-500" />
                你可能感兴趣
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {related.map((item) => (
                  <ArticleCard key={item.id} article={item} />
                ))}
              </div>
            </div>
          </article>

          <aside className="space-y-6">
            <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5 lg:sticky lg:top-24">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-8 h-8 rounded-md bg-primary-500 text-background-50 flex items-center justify-center">
                  <i className="ri-fire-line" />
                </span>
                <h2 className="font-heading font-bold text-base">本周热门</h2>
              </div>
              <div className="mb-5">
                {hotList.map((item, i) => (
                  <ArticleCard key={item.id} article={item} variant="rank" rank={i + 1} />
                ))}
              </div>
              <div className="pt-4 border-t border-background-200">
                <h3 className="font-heading font-bold text-sm mb-3">换个频道看看</h3>
                <div className="flex flex-wrap gap-2">
                  <Link to="/category/entertainment" className="px-3 py-1.5 rounded-full bg-background-100 text-xs hover:text-primary-600 transition cursor-pointer">娱乐八卦</Link>
                  <Link to="/category/celebrity" className="px-3 py-1.5 rounded-full bg-background-100 text-xs hover:text-primary-600 transition cursor-pointer">明星黑料</Link>
                  <Link to="/category/society" className="px-3 py-1.5 rounded-full bg-background-100 text-xs hover:text-primary-600 transition cursor-pointer">社会热点</Link>
                  <Link to="/category/film" className="px-3 py-1.5 rounded-full bg-background-100 text-xs hover:text-primary-600 transition cursor-pointer">影视资讯</Link>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-background-50 border-t border-background-200 px-4 py-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => toggle(article.id)}
          className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium transition cursor-pointer ${
            favorited ? "bg-accent-500 text-foreground-950" : "border border-background-300"
          }`}
        >
          <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"} />
          {favorited ? "已收藏" : "收藏"}
        </button>
        <button
          type="button"
          onClick={() => setShareOpen(true)}
          className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer"
        >
          <i className="ri-share-forward-line" />
          分享
        </button>
      </div>
      <div className="lg:hidden h-16" />

      <SharePanel open={shareOpen} onClose={() => setShareOpen(false)} title={article.title} />
    </SiteLayout>
  );
}